import React, { Suspense, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IconType } from "react-icons";
import {
  PiHouseFill,
  PiFile,
  PiCalendarDots,
  PiVideoCamera,
  PiChatCircleDotsFill,
  PiHandsPraying,
  PiUsers,
  PiShoppingCartSimple,
  PiBookOpen,
  PiMegaphone,
  PiChatsCircle,
  PiGear,
  PiEnvelopeSimple,
  PiArrowLeft,
  PiList,
} from "react-icons/pi";
import { logout } from "../../state/authSlice";
import { clearUser } from "../../state/userSlice";
import { persistor } from "../../state/store";
import { Logo } from "../../images/images";
import AccessDenied from "./AccessDenied";
import useAuth from "../../state/useAuth";
import { canAccessModule, moduleForPath } from "../../pages/admin/admins/adminModules";
import "./AdminLayout.css";
import LoadingComponent from "../loading/Loading";

// react-icons v5 typings don't satisfy JSX element checks on TS 4.4
const icon = (Icon: IconType) =>
  Icon as unknown as React.FC<{ className?: string; "aria-hidden"?: boolean }>;

interface SidebarLink {
  label: string;
  to: string;
  Icon: IconType;
}

const MAIN_LINKS: SidebarLink[] = [
  { label: "Dashboard", to: "/admindashboard", Icon: PiHouseFill },
  { label: "Blog Management", to: "/admin/blog-management", Icon: PiFile },
  { label: "Video Teachings", to: "/admin/video-teachings", Icon: PiVideoCamera },
  { label: "Testimonies", to: "/admin/testimonies", Icon: PiChatCircleDotsFill },
  { label: "Prayer Requests", to: "/admin/prayer-requests", Icon: PiHandsPraying },
  { label: "Salvation", to: "/admin/salvation", Icon: PiUsers },
  { label: "Finance Management", to: "/admin/finance", Icon: PiShoppingCartSimple },
  { label: "Conference Management", to: "/admin/conferences", Icon: PiCalendarDots },
  { label: "Media Spotlight", to: "/admin/media-spotlight", Icon: PiVideoCamera },
  { label: "Digital Library", to: "/admin/library", Icon: PiBookOpen },
  { label: "Events & Announcements", to: "/admin/events", Icon: PiMegaphone },
  { label: "Messages", to: "/admin/messages", Icon: PiChatsCircle },
  { label: "Admin Management", to: "/admin/admins", Icon: PiGear },
];

const MenuIcon = icon(PiList);
const LogoutIcon = icon(PiArrowLeft);

// Used as the parent route of every admin page (see App.tsx), so all of them
// share this one sidebar; the matched page renders through <Outlet />.
const AdminLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLElement>(null);
  const { type, modules, isLoggedIn } = useAuth();
  const pageModule = moduleForPath(pathname);
  const allowed = canAccessModule(type, modules, pageModule);

  // The page scrolls inside <main>, so start each new page at its top
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [pathname]);

  if (!isLoggedIn || (type !== "admin" && type !== "superadmin")) {
    return <Navigate to="/admin/login" replace state={{ from: pathname }} />;
  }

  const handleLogout = async () => {
    dispatch(logout());
    dispatch(clearUser());
    await persistor.purge();
    navigate("/admin/login", { replace: true });
  };

  const renderLink = ({ label, to, Icon }: SidebarLink) => {
    const LinkIcon = icon(Icon);
    return (
      <NavLink
        key={label}
        to={to}
        className={({ isActive }) => `admin-sidebar-link${isActive ? " active" : ""}`}
        onClick={() => setOpen(false)}
      >
        <LinkIcon className="admin-sidebar-icon" aria-hidden />
        <span>{label}</span>
      </NavLink>
    );
  };

  // A full-viewport shell: the sidebar and the page each scroll inside their own
  // container instead of with the window. No top bar; the logo sits in the sidebar.
  return (
    <div className="admin-shell">
    <div className="admin-layout">
      <button
        type="button"
        className="admin-sidebar-toggle"
        onClick={() => setOpen(true)}
        aria-label="Open admin menu"
      >
        <MenuIcon aria-hidden /> Menu
      </button>

      {open && <div className="admin-sidebar-backdrop" onClick={() => setOpen(false)} />}

      <aside className={`admin-sidebar${open ? " open" : ""}`}>
        <div className="admin-sidebar-top">
          <a href="/" className="admin-sidebar-logo" aria-label="Chantal Ekabe Ministries home">
            <img src={Logo} alt="" />
          </a>
          <nav className="admin-sidebar-main">{MAIN_LINKS.map(renderLink)}</nav>
        </div>
        <div className="admin-sidebar-bottom">
          {renderLink({ label: "Email List", to: "/admin/email-list", Icon: PiEnvelopeSimple })}
          <button type="button" className="admin-sidebar-link" onClick={handleLogout}>
            <LogoutIcon className="admin-sidebar-icon" aria-hidden />
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      <main className="admin-layout-content" ref={contentRef}>
        {allowed ? (
          children ?? (
            // Pages are lazy-loaded; keep the sidebar up while one loads
            <Suspense fallback={<LoadingComponent size={120} />}>
              <Outlet />
            </Suspense>
          )
        ) : (
          <AccessDenied moduleLabel={pageModule!.label} />
        )}
      </main>
    </div>
    </div>
  );
};

export default AdminLayout;
